import React, { forwardRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';

const inputVariants = cva(
  'flex w-full rounded-md border bg-background text-sm shadow-sm ring-offset-background transition-[border-color,box-shadow,background-color] duration-fast ease-standard file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-glow disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'border-input',
        filled: 'border-transparent bg-muted/50',
        ghost: 'border-transparent bg-transparent shadow-none',
      },
      uiSize: {
        sm: 'h-9 px-3 py-1.5',
        md: 'h-10 px-3 py-2',
        lg: 'h-11 px-4 py-2.5',
      },
      density: {
        comfortable: '',
        compact: 'text-xs',
      },
      state: {
        default: '',
        invalid: 'border-destructive focus-visible:ring-destructive/30',
        success: 'border-success focus-visible:ring-success/30',
      },
      withIcon: {
        true: 'pl-10',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      uiSize: 'md',
      density: 'comfortable',
      state: 'default',
      withIcon: false,
    },
  }
);

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement>,
    VariantProps<typeof inputVariants> {}

const Input = forwardRef<HTMLInputElement, InputProps>(({
  className,
  type,
  variant,
  uiSize,
  density,
  state,
  withIcon,
  ...props
}, ref) => {
  return <input type={type} className={cn(inputVariants({
    variant,
    uiSize,
    density,
    state,
    withIcon,
  }), className)} ref={ref} {...props} />;
});
Input.displayName = 'Input';
export { Input, inputVariants };